import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const WEB_ERAS = [
  {
    ver: 'Web 1.0', year: '1991–2004', color: '#A8B5C8', icon: 'ri-file-text-line',
    title: '静态只读时代', subtitle: '信息单向流动',
    desc: 'Web 1.0是互联网的原始形态，由Tim Berners-Lee于1991年发明。页面以静态HTML为主，用户只能"读"，无法"写"。内容由少数网站主控制，超链接是唯一的导航方式。',
    logic: [
      { label: '底层协议', value: 'HTTP/1.0 · TCP/IP · DNS' },
      { label: '核心技术', value: 'HTML · CSS · FTP · 静态服务器' },
      { label: '商业模式', value: '门户广告 · 流量变现' },
      { label: '数据主权', value: '服务器端完全控制' },
    ],
    impact: '雅虎、网景、早期搜索引擎崛起，互联网从军用走向民用，信息民主化第一步。',
    img: 'https://public.readdy.ai/ai/img_res/a1b5e15544efa5c98029fc094f047b1b.jpg',
    keyPlayers: ['雅虎', '网景', 'AOL', 'Geocities', 'AltaVista'],
  },
  {
    ver: 'Web 2.0', year: '2004–2020', color: '#FFD700', icon: 'ri-user-shared-line',
    title: '社交互动时代', subtitle: '用户生成内容UGC',
    desc: 'Web 2.0由Tim O\'Reilly于2004年提出，核心是"参与式网络"。用户从被动接受者变为内容创造者。AJAX技术实现页面动态刷新，社交媒体、视频平台、电商平台爆发式增长。',
    logic: [
      { label: '底层协议', value: 'HTTP/2 · WebSocket · REST API' },
      { label: '核心技术', value: 'AJAX · JavaScript框架 · 云计算 · CDN' },
      { label: '商业模式', value: '数据广告 · 平台经济 · 订阅制' },
      { label: '数据主权', value: '平台垄断用户数据（数据殖民主义）' },
    ],
    impact: 'Facebook、YouTube、Twitter、微信、淘宝崛起。平台经济创造数十万亿市值，但数据隐私问题日益严峻。',
    img: 'https://readdy.ai/api/search-image?query=social%20media%20platforms%20web%202.0%20colorful%20icons%20user%20generated%20content%20community%20network%20vibrant%20gold%20yellow%20neon%20dark%20background%20cinematic&width=800&height=450&seq=net-web2-002&orientation=landscape',
    keyPlayers: ['Facebook', 'YouTube', 'Twitter', '微信', '淘宝'],
  },
  {
    ver: 'Web 3.0', year: '2020–2026', color: '#00FF88', icon: 'ri-links-line',
    title: '去中心化时代', subtitle: '区块链 · 数字主权',
    desc: 'Web 3.0以区块链为底层基础设施，实现"去信任化"的价值传输。智能合约自动执行协议，NFT赋予数字资产唯一性，DeFi重构金融体系。用户真正拥有自己的数字资产与身份。',
    logic: [
      { label: '底层协议', value: 'Ethereum · Solana · IPFS · Layer2' },
      { label: '核心技术', value: '智能合约 · NFT · DeFi · DAO · 零知识证明' },
      { label: '商业模式', value: 'Token经济 · Play-to-Earn · 协议费用' },
      { label: '数据主权', value: '用户自主控制私钥=控制资产与身份' },
    ],
    impact: 'DeFi锁仓量峰值超2000亿美元，NFT市场年交易额超400亿，元宇宙概念引爆全球资本市场。',
    img: 'https://readdy.ai/api/search-image?query=blockchain%20decentralized%20network%20nodes%20glowing%20green%20neon%20web3%20cryptocurrency%20NFT%20DeFi%20digital%20art%20dark%20background%20futuristic%20cinematic&width=800&height=450&seq=net-web3-002&orientation=landscape',
    keyPlayers: ['Ethereum', 'OpenSea', 'Uniswap', 'Axie Infinity', 'Decentraland'],
  },
  {
    ver: 'Web 4.0', year: '2026–2030', color: '#FF6B9D', icon: 'ri-brain-line',
    title: '智能语义时代', subtitle: 'AI驱动 · 万物互联',
    desc: 'Web 4.0是"超级智能网络"，AI大模型成为网络的神经系统，语义理解让机器真正读懂人类意图。物联网设备突破1000亿，数字孪生城市全面落地，虚实边界开始模糊。',
    logic: [
      { label: '底层协议', value: 'Semantic Web · AI Agent协议 · 6G网络切片' },
      { label: '核心技术', value: 'AGI · 数字孪生 · 边缘计算 · 脑机接口初代' },
      { label: '商业模式', value: 'AI即服务 · 数字孪生订阅 · 意图经济' },
      { label: '数据主权', value: 'AI代理代表用户自主决策与交互' },
    ],
    impact: '预计2028年全球AI市场规模超15万亿美元，数字孪生城市覆盖全球TOP100城市，元宇宙日活用户突破10亿。',
    img: 'https://readdy.ai/api/search-image?query=AI%20artificial%20intelligence%20semantic%20web%20smart%20city%20digital%20twin%20holographic%20interface%20pink%20neon%20glow%20dark%20background%20futuristic%20cinematic%208k&width=800&height=450&seq=net-web4-002&orientation=landscape',
    keyPlayers: ['OpenAI', 'Google DeepMind', 'META66', 'NVIDIA', 'Anthropic'],
  },
  {
    ver: 'Web 5.0', year: '2030+', color: '#00FFCC', icon: 'ri-global-line',
    title: '虚实融合时代', subtitle: '数据主权 · 意识互联',
    desc: 'Web 5.0是Jack Dorsey提出的"去中心化网络+数字身份主权"终极形态。结合脑机接口、量子通信与全息投影，人类将在虚实完全融合的空间中生活、工作与社交。',
    logic: [
      { label: '底层协议', value: '量子通信协议 · 脑机接口标准 · 去中心化身份DID' },
      { label: '核心技术', value: '量子计算 · 脑机接口 · 裸眼3D · 全息投影' },
      { label: '商业模式', value: '意识经济 · 虚实资产互通 · 数字永生服务' },
      { label: '数据主权', value: '个人完全掌控数字身份与意识数据' },
    ],
    impact: '预计2035年全球元宇宙经济规模超80万亿美元，虚拟世界GDP超越多数实体国家。',
    img: 'https://readdy.ai/api/search-image?query=web5%20metaverse%20virtual%20reality%20brain%20computer%20interface%20quantum%20holographic%20world%20cyan%20neon%20glow%20dark%20background%20futuristic%20cinematic%208k%20ultra%20detailed&width=800&height=450&seq=net-web5-002&orientation=landscape',
    keyPlayers: ['META66', 'Jack Dorsey TBD', '量子通信联盟', '脑机接口标准组织', '全球数字身份联盟'],
  },
];

const NETWORK_GENS = [
  {
    gen: '5G', year: '2019–2025', color: '#A8B5C8', speed: '10 Gbps', latency: '1ms', density: '100万/km²',
    title: '万物互联基础', icon: 'ri-signal-tower-line',
    desc: '5G实现了移动互联网从"人联"到"物联"的跨越。eMBB（增强移动宽带）、mMTC（海量机器通信）、uRLLC（超可靠低延迟）三大场景奠定了物联网基础，但仍无法满足元宇宙的实时渲染需求。',
    bottleneck: '频谱效率瓶颈 · 基站密度不足 · 能耗过高 · 无法支撑全息通信',
    keyTech: ['毫米波mmWave', 'Massive MIMO', '网络切片', '边缘计算MEC', 'D2D通信'],
    metaverse: '基础物联网 · 低清XR',
    img: 'https://readdy.ai/api/search-image?query=5G%20network%20tower%20city%20infrastructure%20glowing%20silver%20signal%20waves%20futuristic%20technology%20dark%20background%20sci-fi%20cinematic%208k&width=700&height=400&seq=net-5g-002&orientation=landscape',
  },
  {
    gen: '6G', year: '2025–2030', color: '#00FF88', speed: '1 Tbps', latency: '<0.1ms', density: '1000万/km²',
    title: '元宇宙基础设施', icon: 'ri-signal-tower-fill',
    desc: '6G工作在太赫兹（THz）频段，峰值速率达1Tbps，是5G的100倍。空天地海一体化网络实现全球无缝覆盖。AI原生网络架构让基站具备自主学习能力，网络切片技术为元宇宙提供专属QoS保障。',
    bottleneck: '太赫兹信号穿透力弱 · 基站建设成本高 · 全球频谱协调难度大',
    keyTech: ['太赫兹通信（0.1-10THz）', 'AI原生网络架构', '空天地海一体化', '智能超表面RIS', '语义通信'],
    metaverse: '全息通信 · 实时渲染',
    img: 'https://readdy.ai/api/search-image?query=6G%20terahertz%20network%20futuristic%20green%20neon%20glowing%20signal%20waves%20space%20satellite%20earth%20coverage%20technology%20dark%20background%20sci-fi%20cinematic%208k%20ultra%20detailed&width=700&height=400&seq=net-6g-002&orientation=landscape',
  },
  {
    gen: '7G', year: '2030–2035', color: '#FF6B9D', speed: '10 Tbps', latency: '<0.01ms', density: '1亿/km²',
    title: '意识级通信网络', icon: 'ri-brain-line',
    desc: '7G将实现"意识级"通信——传输的不再是比特流，而是人类感知与意识的数字映射。量子通信与经典通信融合，脑机接口标准化接入，全息投影实时传输成为现实。神经网络直接与互联网交互。',
    bottleneck: '量子纠错技术尚未成熟 · 脑机接口伦理争议 · 意识数据安全',
    keyTech: ['量子-经典混合通信', '脑机接口标准协议', '全息实时传输', '意识数字化编码', '零延迟感知网络'],
    metaverse: '意识互联 · 完全沉浸',
    img: 'https://readdy.ai/api/search-image?query=7G%20quantum%20consciousness%20network%20brain%20neural%20interface%20pink%20neon%20glow%20futuristic%20technology%20dark%20background%20sci-fi%20cinematic%208k%20ultra%20detailed%20holographic&width=700&height=400&seq=net-7g-002&orientation=landscape',
  },
];

export default function NetworkPage() {
  const navigate = useNavigate();
  const [activeWeb, setActiveWeb] = useState(2);
  const [activeGen, setActiveGen] = useState(1);
  const [animIn, setAnimIn] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'META66网络演进全景 | Web1.0到Web5.0 · 5G到7G底层逻辑深度解析';
    setTimeout(() => setAnimIn(true), 100);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const nodes: { x: number; y: number; vx: number; vy: number; r: number; color: string }[] = [];
    const colors = ['rgba(0,255,136,0.4)', 'rgba(255,215,0,0.3)', 'rgba(255,107,157,0.3)', 'rgba(0,255,204,0.3)'];
    for (let i = 0; i < 40; i++) {
      nodes.push({ x: Math.random() * canvas.width, y: Math.random() * canvas.height, vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3, r: Math.random() * 2 + 1, color: colors[Math.floor(Math.random() * colors.length)] });
    }
    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      nodes.forEach((n, i) => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx = -n.vx;
        if (n.y < 0 || n.y > canvas.height) n.vy = -n.vy;
        nodes.forEach((m, j) => {
          if (i >= j) return;
          const dx = n.x - m.x; const dy = n.y - m.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(n.x, n.y);
            ctx.lineTo(m.x, m.y);
            ctx.strokeStyle = `rgba(0,255,136,${0.08 * (1 - dist / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = n.color;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, []);

  const currentWeb = WEB_ERAS[activeWeb];
  const currentGen = NETWORK_GENS[activeGen];

  return (
    <div className="min-h-screen overflow-hidden" style={{ background: '#020810', fontFamily: "'Inter', 'PingFang SC', sans-serif" }}>
      <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />

      {/* Fixed BG */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 opacity-4" style={{
          backgroundImage: 'linear-gradient(rgba(0,255,136,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,136,0.08) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(0,255,136,0.06) 0%, transparent 50%)' }} />
      </div>

      {/* Header */}
      <div className="sticky top-0 z-50" style={{ background: 'rgba(2,8,16,0.95)', borderBottom: '1px solid rgba(0,255,136,0.15)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap hover:scale-105" style={{ color: 'rgba(255,255,255,0.5)' }}>
            <i className="ri-arrow-left-line" />
            <span className="text-sm">返回首页</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center" style={{ background: 'rgba(0,255,136,0.15)', border: '1.5px solid rgba(0,255,136,0.4)', borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
              <i className="ri-signal-tower-line text-white text-sm" />
            </div>
            <span className="font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '0.9rem' }}>NETWORK EVOLUTION</span>
          </div>
          <div className="text-sm" style={{ color: 'rgba(0,255,136,0.6)', fontFamily: 'Caveat, cursive' }}>Web 1.0 → Web 5.0</div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative z-10 h-[420px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=internet%20evolution%20web%20network%20technology%20panorama%20glowing%20neon%20green%20connections%20nodes%20dark%20background%20futuristic%20cinematic%20wide%208k%20ultra%20detailed&width=1920&height=500&seq=network-hero-002&orientation=landscape"
          alt="网络演进"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(2,8,16,0.3) 0%, rgba(2,8,16,0.6) 60%, rgba(2,8,16,1) 100%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(0,255,136,0.1) 0%, transparent 60%)' }} />

        <div className="absolute inset-0 flex items-center px-8 md:px-16">
          <div style={{ opacity: animIn ? 1 : 0, transform: animIn ? 'translateX(0)' : 'translateX(-40px)', transition: 'all 0.9s ease' }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-5" style={{ background: 'rgba(0,255,136,0.12)', border: '1.5px solid rgba(0,255,136,0.35)', borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%' }}>
              <span className="w-2 h-2 rounded-full bg-[#00FF88] animate-pulse" />
              <span className="text-sm font-bold" style={{ color: '#00FF88', fontFamily: 'Caveat, cursive', fontSize: '0.95rem' }}>Web 1.0 → Web 5.0 · 5G → 7G</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-4 leading-tight" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FF88] to-[#00FFCC]">NETWORK</span>
              <br />
              <span className="text-white text-3xl md:text-4xl">EVOLUTION</span>
            </h1>
            <p className="text-white/55 text-lg max-w-2xl leading-relaxed">从静态只读到意识互联，从5G到7G——深度解析互联网与通信网络的底层逻辑与演化进程</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-16">

        {/* ===== Web Evolution ===== */}
        <div className="mb-24">
          <div className="flex items-center gap-4 mb-10">
            <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,136,0.4))' }} />
            <h2 className="text-3xl font-black text-white whitespace-nowrap" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <span style={{ color: '#00FF88' }}>Web</span> 演进全景图
            </h2>
            <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, rgba(0,255,136,0.4), transparent)' }} />
          </div>

          {/* Web Era Tabs */}
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            {WEB_ERAS.map((w, i) => (
              <button
                key={w.ver}
                onClick={() => setActiveWeb(i)}
                className="px-5 py-2.5 font-bold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{
                  background: activeWeb === i ? `${w.color}20` : 'rgba(255,255,255,0.03)',
                  color: activeWeb === i ? w.color : 'rgba(255,255,255,0.35)',
                  border: `1.5px solid ${activeWeb === i ? w.color + '60' : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                  boxShadow: activeWeb === i ? `0 0 20px ${w.color}25` : 'none',
                  fontFamily: 'Caveat, cursive',
                  fontSize: '0.95rem',
                }}
              >
                {w.ver} <span className="opacity-50 text-xs ml-1">{w.year.split('–')[0]}</span>
              </button>
            ))}
          </div>

          {/* Web Era Detail */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
            <div className="relative rounded-2xl overflow-hidden" style={{ minHeight: '320px', border: `1.5px solid ${currentWeb.color}20`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
              <img src={currentWeb.img} alt={currentWeb.title} className="w-full h-full object-cover object-top" style={{ minHeight: '320px' }} />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 30%, rgba(2,8,16,0.9) 100%)' }} />
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1.5 text-xs font-bold" style={{ background: `${currentWeb.color}15`, color: currentWeb.color, border: `1.5px solid ${currentWeb.color}40`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', fontFamily: 'Caveat, cursive' }}>
                  {currentWeb.ver} · {currentWeb.year}
                </span>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="text-white text-2xl font-black mb-1">{currentWeb.title}</h3>
                <p className="text-sm font-bold" style={{ color: currentWeb.color, fontFamily: 'Caveat, cursive' }}>{currentWeb.subtitle}</p>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px' }}>
                <p className="text-white/60 leading-relaxed text-sm">{currentWeb.desc}</p>
              </div>
              <div className="p-5 rounded-2xl" style={{ background: `${currentWeb.color}05`, border: `1px solid ${currentWeb.color}15`, borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px' }}>
                <h4 className="text-white/60 text-xs font-bold mb-3">底层逻辑解析</h4>
                <div className="grid grid-cols-2 gap-3">
                  {currentWeb.logic.map(l => (
                    <div key={l.label} className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <p className="text-white/35 text-xs mb-1">{l.label}</p>
                      <p className="text-white/70 text-xs font-semibold leading-relaxed">{l.value}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4 rounded-xl" style={{ borderLeft: `3px solid ${currentWeb.color}`, background: `${currentWeb.color}05` }}>
                <p className="text-xs font-bold mb-1" style={{ color: currentWeb.color }}>历史影响</p>
                <p className="text-white/55 text-sm leading-relaxed">{currentWeb.impact}</p>
              </div>
              <div>
                <p className="text-white/30 text-xs font-bold mb-2">代表性玩家</p>
                <div className="flex flex-wrap gap-2">
                  {currentWeb.keyPlayers.map(p => (
                    <span key={p} className="px-3 py-1 text-xs font-medium" style={{ background: `${currentWeb.color}10`, color: currentWeb.color, border: `1px solid ${currentWeb.color}25`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', fontFamily: 'Caveat, cursive' }}>{p}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Web Timeline Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {WEB_ERAS.map((w, i) => {
              const detailId = w.ver === 'Web 1.0' ? 'web-1' : w.ver === 'Web 2.0' ? 'web-2' : w.ver === 'Web 3.0' ? 'web-3' : w.ver === 'Web 4.0' ? 'web-4' : 'web-5';
              return (
                <div
                  key={w.ver}
                  className="flex flex-col items-center text-center gap-2 p-4 rounded-2xl transition-all duration-300 cursor-pointer hover:-translate-y-1"
                  style={{
                    border: `1.5px solid ${activeWeb === i ? w.color + '50' : 'rgba(255,255,255,0.06)'}`,
                    background: activeWeb === i ? `${w.color}08` : 'rgba(255,255,255,0.02)',
                    borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px',
                  }}
                  onClick={() => setActiveWeb(i)}
                >
                  <div className="w-10 h-10 flex items-center justify-center" style={{ background: activeWeb === i ? `${w.color}20` : 'rgba(255,255,255,0.05)', borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                    <i className={`${w.icon} text-lg`} style={{ color: activeWeb === i ? w.color : 'rgba(255,255,255,0.2)' }} />
                  </div>
                  <p className="font-black text-sm" style={{ color: activeWeb === i ? w.color : 'rgba(255,255,255,0.3)', fontFamily: 'Orbitron, sans-serif', fontSize: '0.75rem' }}>{w.ver}</p>
                  <p className="text-white/25 text-xs">{w.year}</p>
                  <p className="text-white/35 text-xs leading-tight">{w.subtitle}</p>
                  <button
                    onClick={e => { e.stopPropagation(); navigate(`/network/${detailId}`); }}
                    className="mt-1 px-3 py-1 text-xs font-bold rounded-lg whitespace-nowrap transition-all hover:scale-105 cursor-pointer"
                    style={{ background: `${w.color}15`, border: `1px solid ${w.color}35`, color: w.color }}
                  >
                    深度解析
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* ===== Network Generation ===== */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-10">
            <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,136,0.4))' }} />
            <h2 className="text-3xl font-black text-white whitespace-nowrap" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              5G → 6G → 7G <span style={{ color: '#00FF88' }}>底层逻辑</span>
            </h2>
            <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, rgba(0,255,136,0.4), transparent)' }} />
          </div>

          {/* Gen Selector */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
            {NETWORK_GENS.map((g, i) => {
              const genDetailId = g.gen === '5G' ? '5g' : g.gen === '6G' ? '6g' : '7g';
              return (
                <div key={g.gen} className="flex flex-col items-center gap-2">
                  <button
                    onClick={() => setActiveGen(i)}
                    className="flex items-center gap-3 px-8 py-3 font-bold transition-all duration-300 whitespace-nowrap cursor-pointer"
                    style={{
                      background: activeGen === i ? `${g.color}20` : 'rgba(255,255,255,0.03)',
                      color: activeGen === i ? g.color : 'rgba(255,255,255,0.35)',
                      border: `1.5px solid ${activeGen === i ? g.color + '60' : 'rgba(255,255,255,0.08)'}`,
                      borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                      boxShadow: activeGen === i ? `0 0 25px ${g.color}25` : 'none',
                      fontFamily: 'Caveat, cursive',
                      fontSize: '1.1rem',
                    }}
                  >
                    <i className={`${g.icon} text-sm`} />
                    <span className="font-black" style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '1rem' }}>{g.gen}</span>
                    <span className="text-xs opacity-60">{g.year}</span>
                  </button>
                  <button
                    onClick={() => navigate(`/network/${genDetailId}`)}
                    className="px-4 py-1.5 text-xs font-bold rounded-lg whitespace-nowrap transition-all hover:scale-105 cursor-pointer"
                    style={{ background: `${g.color}15`, border: `1px solid ${g.color}35`, color: g.color }}
                  >
                    深度解析 →
                  </button>
                </div>
              );
            })}
          </div>

          {/* Gen Detail */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
            {/* Left: Metrics */}
            <div className="flex flex-col gap-4">
              <div className="p-6 rounded-2xl" style={{ background: `${currentGen.color}06`, border: `1.5px solid ${currentGen.color}20`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center" style={{ background: `${currentGen.color}15`, border: `2px solid ${currentGen.color}35`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                    <i className={`${currentGen.icon} text-2xl`} style={{ color: currentGen.color }} />
                  </div>
                  <div>
                    <p className="font-black text-xl" style={{ color: currentGen.color, fontFamily: 'Orbitron, sans-serif' }}>{currentGen.gen}</p>
                    <p className="text-white/35 text-xs">{currentGen.year}</p>
                  </div>
                </div>
                {[
                  { label: '峰值速率', value: currentGen.speed, icon: 'ri-speed-line' },
                  { label: '端到端延迟', value: currentGen.latency, icon: 'ri-timer-flash-line' },
                  { label: '连接密度', value: currentGen.density, icon: 'ri-router-line' },
                ].map(m => (
                  <div key={m.label} className="flex items-center gap-3 mb-4 last:mb-0">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ background: `${currentGen.color}12`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                      <i className={`${m.icon} text-sm`} style={{ color: currentGen.color }} />
                    </div>
                    <div>
                      <p className="text-white/35 text-xs">{m.label}</p>
                      <p className="font-black text-base" style={{ color: currentGen.color, fontFamily: 'Orbitron, sans-serif' }}>{m.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255,180,0,0.06)', border: '1px solid rgba(255,180,0,0.15)' }}>
                <p className="text-yellow-400/70 text-xs font-bold mb-2 flex items-center gap-1.5">
                  <i className="ri-error-warning-line" />当前瓶颈
                </p>
                <p className="text-yellow-300/55 text-xs leading-relaxed">{currentGen.bottleneck}</p>
              </div>
            </div>

            {/* Center: Image + Desc */}
            <div className="flex flex-col gap-4">
              <div className="relative rounded-2xl overflow-hidden h-52" style={{ border: `1.5px solid ${currentGen.color}20`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <img src={currentGen.img} alt={currentGen.gen} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 40%, rgba(2,8,16,0.8) 100%)' }} />
                <div className="absolute bottom-4 left-4">
                  <p className="text-white font-bold">{currentGen.title}</p>
                </div>
              </div>
              <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px' }}>
                <p className="text-white/55 text-sm leading-relaxed">{currentGen.desc}</p>
              </div>
            </div>

            {/* Right: Key Tech */}
            <div className="flex flex-col gap-4">
              <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px' }}>
                <p className="text-white/35 text-xs font-bold mb-3 flex items-center gap-1.5">
                  <i className="ri-flashlight-line" style={{ color: currentGen.color }} />核心关键技术
                </p>
                <div className="flex flex-wrap gap-2">
                  {currentGen.keyTech.map(t => (
                    <span key={t} className="px-3 py-1.5 text-xs font-semibold" style={{ border: `1px solid ${currentGen.color}30`, color: currentGen.color, background: `${currentGen.color}08`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', fontFamily: 'Caveat, cursive' }}>{t}</span>
                  ))}
                </div>
              </div>
              <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px' }}>
                <p className="text-white/35 text-xs font-bold mb-3">与元宇宙的关系</p>
                <div className="flex flex-col gap-3">
                  {[
                    { label: '支撑能力', value: currentGen.metaverse },
                    { label: '元宇宙阶段', value: currentGen.gen === '5G' ? 'Web3.0初期' : currentGen.gen === '6G' ? 'Web4.0爆发期' : 'Web5.0终极形态' },
                    { label: '用户体验', value: currentGen.gen === '5G' ? '低分辨率VR' : currentGen.gen === '6G' ? '8K全息投影' : '意识级沉浸' },
                  ].map(r => (
                    <div key={r.label} className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                      <p className="text-white/30 text-xs mb-1">{r.label}</p>
                      <p className="text-white/65 text-xs font-semibold">{r.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
            <div className="p-5" style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <h3 className="text-white font-bold">三代网络核心指标对比</h3>
            </div>
            <div className="p-5" style={{ background: 'rgba(255,255,255,0.01)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-24 flex-shrink-0" />
                {NETWORK_GENS.map(g => (
                  <div key={g.gen} className="flex-1 text-center font-black text-sm" style={{ color: g.color, fontFamily: 'Orbitron, sans-serif' }}>{g.gen}</div>
                ))}
              </div>
              {[
                { label: '峰值速率', vals: ['10 Gbps', '1 Tbps', '10 Tbps'] },
                { label: '端到端延迟', vals: ['1ms', '<0.1ms', '<0.01ms'] },
                { label: '连接密度', vals: ['100万/km²', '1000万/km²', '1亿/km²'] },
                { label: '频谱范围', vals: ['毫米波 mmWave', '太赫兹 THz', '量子+THz'] },
                { label: '元宇宙支撑', vals: ['基础物联网', '全息通信', '意识互联'] },
              ].map(row => (
                <div key={row.label} className="flex items-center gap-3 mb-3">
                  <span className="text-white/30 text-xs w-24 flex-shrink-0">{row.label}</span>
                  {row.vals.map((v, vi) => (
                    <div key={vi} className="flex-1 text-center py-2 px-2 text-xs font-semibold" style={{ background: `${NETWORK_GENS[vi].color}08`, color: NETWORK_GENS[vi].color, border: `1px solid ${NETWORK_GENS[vi].color}20`, borderRadius: '8px' }}>{v}</div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {[
            { label: '全球6G专利', value: '12万+', color: '#00FF88', icon: 'ri-award-line' },
            { label: '研究机构', value: '500+', color: '#FFD700', icon: 'ri-building-line' },
            { label: '商用时间', value: '2025年', color: '#FF6B9D', icon: 'ri-calendar-line' },
            { label: '覆盖目标', value: '全球100%', color: '#00FFCC', icon: 'ri-earth-line' },
          ].map(stat => (
            <div key={stat.label} className="p-6 rounded-2xl text-center" style={{ background: `${stat.color}06`, border: `1.5px solid ${stat.color}20`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3" style={{ background: `${stat.color}12`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                <i className={`${stat.icon} text-xl`} style={{ color: stat.color }} />
              </div>
              <div className="text-3xl font-black mb-1" style={{ color: stat.color, fontFamily: 'Orbitron, sans-serif' }}>{stat.value}</div>
              <div className="text-white/40 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
